@if (isStandaloneDialog()) {
  <umss-dialog-header
    title="Negotiation history"
    [showCloseButton]="true"
    (onClose)="closed.emit()"
  />
} @else {
  <p class="pg-small-regular inline-title">Negotiation history</p>
}

<div class="modal-body" [class.inline-mode]="!isStandaloneDialog()">
  @if (isLoading()) {
    <div @fadeIn class="history-list skeleton-mode">
      @for (i of [1, 2, 3]; track i) {
        <div class="history-item">
          <div class="item-header">
            <div class="skeleton-title">
              <umss-skeleton
                variant="Line"
                [spacingY]="6"
                [styles]="{ maxWidth: '100px' }"
              />
              <umss-skeleton
                variant="Line"
                [spacingY]="6"
                [styles]="{ maxWidth: '60px' }"
              />
            </div>
            <umss-skeleton
              variant="Line"
              [spacingY]="4"
              [styles]="{ maxWidth: '120px' }"
            />
          </div>
          <umss-skeleton
            variant="Line"
            [spacingY]="5"
            [styles]="{ maxWidth: '90%' }"
          />
          <umss-skeleton
            variant="Line"
            [spacingY]="5"
            [styles]="{ maxWidth: '60%' }"
          />
        </div>
      }
    </div>
  } @else if (error()) {
    <div @fadeIn>
      <umss-alert
        type="Error"
        variant="soft"
        [line1]="error() || ''"
        [fullWidth]="true"
      />
    </div>
  } @else if (!negotiation() || negotiation()!.offers.length === 0) {
    <div @fadeIn>
      <umss-empty-state
        iconName="phosphorHandshake"
        title="No negotiation history"
        description="No counter-offers have been exchanged yet."
        [showButton]="false"
      />
    </div>
  } @else {
    <ul @fadeIn class="history-list">
      @for (
        offer of negotiation()!.offers;
        track offer.id;
        let isLast = $last
      ) {
        @let isLatestActive =
          isLast && negotiation()!.isNegotiationActive === true;
        <li>
          <umss-card
            asTag="article"
            variant="Outlined"
            padding="Small"
            [class.card-mine]="offer.proposedByRole === openedBy()"
            [class.card-latest-theirs]="
              isLatestActive && offer.proposedByRole !== openedBy()
            "
            [class.card-latest-mine]="
              isLatestActive && offer.proposedByRole === openedBy()
            "
          >
            <div class="item-header">
              <span class="offered-by">{{ labelFor(offer) }}</span>
              <span class="rate">
                {{
                  offer.proposedRate | currency: 'USD' : 'symbol' : '1.2-2'
                }}/hr
              </span>
              @if (isLatestActive) {
                <umss-badge
                  [label]="
                    isMyTurn() ? 'Awaiting your response' : 'Awaiting response'
                  "
                  [variant]="isMyTurn() ? 'warning' : 'neutral'"
                  size="small"
                />
              }
              <span class="date">{{
                offer.createdAt | date: 'MMM d, y, h:mm a'
              }}</span>
            </div>

            @if (offer.message) {
              <p class="message">{{ offer.message }}</p>
            }
          </umss-card>
        </li>
      }
    </ul>

    @if (isMyTurn()) {
      @if (isShowingCounterForm()) {
        <form
          smoothCollapse
          class="counter-form"
          [formGroup]="counterForm"
          (ngSubmit)="onSubmitCounterOffer()"
        >
          <umss-input
            label="Proposed rate ($/hr)"
            type="text"
            inputmode="decimal"
            [min]="1"
            [max]="500"
            suffix="/hr"
            prefixIconName="phosphorCurrencyDollar"
            placeholder="e.g. 65.00"
            formControlName="proposedRate"
            [error]="!!proposedRateError"
            [errorMessage]="proposedRateError ?? ''"
          />

          <umss-textarea
            label="Message (optional)"
            [placeholder]="'Add a note for the ' + otherPartyLabel + '...'"
            [maxLength]="messageMaxLength"
            [rows]="3"
            formControlName="message"
            [error]="!!messageError"
            [errorMessage]="messageError ?? ''"
          />

          @if (!isStandaloneDialog()) {
            <div class="counter-form-actions">
              <umss-button
                label="Cancel"
                variant="Ghost"
                size="Regular"
                [fullWidth]="true"
                [disabled]="isSubmitting()"
                (onClick)="onToggleCounterForm()"
              />
              <umss-button
                label="Send proposal"
                variant="Primary"
                size="Regular"
                type="submit"
                [fullWidth]="true"
                [loading]="isSubmitting()"
                [disabled]="isSubmitting()"
              />
            </div>
          }
        </form>
      } @else if (!isStandaloneDialog()) {
        <div smoothCollapse class="action-buttons">
          <umss-button
            label="Accept proposal"
            variant="Primary"
            size="Regular"
            leftIconName="phosphorCheck"
            [fullWidth]="true"
            [loading]="isSubmitting()"
            [disabled]="isSubmitting()"
            (onClick)="onAccept()"
          />
          <umss-button
            label="Counter-offer"
            variant="Outline"
            size="Regular"
            leftIconName="phosphorArrowsLeftRight"
            [fullWidth]="true"
            [disabled]="isSubmitting()"
            (onClick)="onToggleCounterForm()"
          />
        </div>
      }
    }

    @if (isWaitingForOtherParty()) {
      <div smoothCollapse class="waiting-message">
        <umss-alert
          type="Info"
          variant="soft"
          [line1]="'Waiting for the ' + otherPartyLabel + '\'s response...'"
          [fullWidth]="true"
        />
      </div>
    }
  }
</div>

@if (isStandaloneDialog()) {
  <umss-dialog-footer
    [primaryButtonLabel]="footerPrimaryLabel()"
    [primaryButtonLeftIconName]="footerPrimaryIcon()"
    [secondaryButtonLabel]="footerSecondaryLabel()"
    [secondaryButtonLeftIconName]="footerSecondaryIcon()"
    [loading]="isSubmitting()"
    [primaryButtonDisabled]="isSubmitting()"
    [secondaryButtonDisabled]="isSubmitting()"
    (onPrimaryClick)="onFooterPrimaryClick()"
    (onSecondaryClick)="onFooterSecondaryClick()"
  />
}
